/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React, { useState, useMemo } from 'react';

import { Colors } from 'src/common/ui/base/Colors';
import { Spacing } from 'src/common/ui/base/Spacing';
import { BoulderIcon } from 'src/common/ui/components/BoulderIcon';
import { Pagination } from 'src/common/ui/components/Pagination';
import { Table, Row, Cell } from 'src/common/ui/components/Table';

type DataItem = { id: string | number; onClick?: () => void };

type SmartTableProps<T extends DataItem> = {
  className?: string;
  columns: Array<{
    key: keyof T;
    label: string | React.ReactNode; // Allow both strings and React elements
    sortable?: boolean;
  }>;
  controlled?: boolean;
  currentPage?: number;
  data: T[];
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rows: number) => void;
  onSortChange?: (key: keyof T, direction: SortDirection) => void;
  rowsPerPage?: number;
  rowsPerPageOptions?: number[];
  totalRows?: number | null;
};

enum SortDirection {
  Ascending = 'asc',
  Descending = 'desc',
}

export const SmartTable = <T extends DataItem>({
  className,
  columns,
  currentPage: controlledPage,
  data,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
  rowsPerPage: controlledRowsPerPage,
  rowsPerPageOptions,
  controlled = false,
  totalRows: controlledTotalRows,
}: SmartTableProps<T>) => {
  const [localPage, setLocalPage] = useState(controlledPage ?? 1);
  const [rowsPerPage, setRowsPerPage] = useState(controlledRowsPerPage ?? 10);
  const [sortKey, setSortKey] = useState<keyof T | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.Ascending);

  const currentPage = controlledPage ?? localPage;

  const handlePageChange = (page: number) => {
    setLocalPage(page);
    if (onPageChange) {
      onPageChange(page);
    }
  };

  const handleRowsPerPageChange = (rows: number) => {
    const currentTopRowIndex = (currentPage - 1) * rowsPerPage;
    const newPage = Math.floor(currentTopRowIndex / rows) + 1;

    setRowsPerPage(rows);
    handlePageChange(newPage);

    if (onRowsPerPageChange) {
      onRowsPerPageChange(rows);
    }
  };

  const handleSortChange = (key: keyof T) => {
    const newDirection =
      sortKey === key && sortDirection === SortDirection.Ascending
        ? SortDirection.Descending
        : SortDirection.Ascending;
    setSortKey(key);
    setSortDirection(newDirection);
    handlePageChange(1);

    if (onSortChange) {
      onSortChange(key, newDirection);
    }
  };

  const sortedData = useMemo(() => {
    if (controlled || !sortKey) return data;

    return [...data].sort((a, b) => {
      const aValue = a[sortKey];
      const bValue = b[sortKey];

      if (aValue < bValue) return sortDirection === SortDirection.Ascending ? -1 : 1;
      if (aValue > bValue) return sortDirection === SortDirection.Ascending ? 1 : -1;
      return 0;
    });
  }, [data, sortKey, sortDirection, controlled]);

  const paginatedData = useMemo(() => {
    if (controlled) return data;

    const startIndex = (currentPage - 1) * rowsPerPage;
    return sortedData.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedData, currentPage, rowsPerPage, controlled, data]);

  return (
    <div data-testid="nds-component-smart-table">
      <Table className={className}>
        <Row>
          {columns.map(column => (
            <Cell key={column.key as string} isHeader>
              <button
                type="button"
                onClick={() => column.sortable && handleSortChange(column.key)}
                css={css`
                  ${sortableButtonStyles};

                  ${column.sortable &&
                  sortKey === column.key &&
                  sortDirection === SortDirection.Ascending
                    ? css`
                        figure svg {
                          transform: rotate(180deg);
                        }
                      `
                    : undefined}
                `}
                disabled={!column.sortable}
              >
                {column.label}
                {sortKey === column.key && (
                  <BoulderIcon
                    css={css`
                      ${arrowStyles}
                      transform: 
                        rotate(${sortDirection === SortDirection.Ascending ? '180' : '0'}deg)
                        translateY(${sortDirection === SortDirection.Ascending ? '-1' : '1'}px);
                    `}
                    icon="arrowDropDown"
                    size={24}
                  />
                )}
              </button>
            </Cell>
          ))}
        </Row>
        {paginatedData.map(row => (
          <Row key={row.id} onClick={row.onClick}>
            {columns.map(column => (
              <Cell key={column.key as string}>{row[column.key]}</Cell>
            ))}
          </Row>
        ))}
      </Table>
      <Pagination
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        totalRows={controlled ? controlledTotalRows : data.length}
        rowsPerPageOptions={rowsPerPageOptions}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </div>
  );
};

const sortableButtonStyles = css`
  cursor: pointer;
  border: none;
  background: none;
  padding: 0;
  display: block;
  font-size: inherit;
  font-weight: inherit;
  user-select: none;

  &:disabled {
    cursor: default;
  }
`;

const arrowStyles = css`
  display: inline;
  margin: 0;
  margin-left: ${Spacing.Unit2}px;
  margin-bottom: -1px;
  padding: 0;
  vertical-align: middle;
  position: relative;
  top: -2px; // Visually center the arrow with the text

  path {
    fill: ${Colors.CoolGray6};
  }
`;
